/*
 * Hiring v2 (owner, 2026-11-04: "yes build all"): the stages, why someone is not a fit, and every text an applicant gets,
 * in English or Spanish. Most hourly applicants never read email and take the first job that texts back, so the careers
 * page texts a link to pick a phone screen time, reminds them, and closes out kindly. Research and plan in the vault
 * (project-hiring-pipeline). The crew are 1099 today (owner), so paperwork asks for a W-9, not a W-4 or an I-9.
 */
import { z } from "zod";

export const APPLY_LANGS = ["en", "es"] as const;
export type ApplyLang = (typeof APPLY_LANGS)[number];

/** Why someone left the board, for the reports. Never their language, age or where they are from. */
export const NO_FIT_REASONS = ["no_answer", "no_show", "schedule", "no_ride", "pay", "background", "other"] as const;
export type NoFitReason = (typeof NO_FIT_REASONS)[number];
export const NO_FIT_WORDS: Record<NoFitReason, string> = {
  no_answer: "Never answered",
  no_show: "Did not show",
  schedule: "Days do not fit",
  no_ride: "No ride",
  pay: "Pay did not fit",
  background: "Background check",
  other: "Other",
};

/** The phone screen scorecard, each 1 to 5, so everyone rates the same way. */
export const SCREEN_SCORES = ["onTime", "talks", "experience", "days", "hire"] as const;
export type ScreenScore = (typeof SCREEN_SCORES)[number];
export const SCREEN_SCORE_WORDS: Record<ScreenScore, string> = {
  onTime: "Picked up on time",
  talks: "Easy to talk to",
  experience: "Cleaning or crew work before",
  days: "Days and hours fit",
  hire: "Would you hire them",
};
export const screenScores = z.partialRecord(z.enum(SCREEN_SCORES), z.number().int().min(1).max(5));
export type ScreenScores = z.infer<typeof screenScores>;

/** Weekdays, 1 Monday to 7 Sunday, in each language. */
export const WEEKDAY_WORDS: Record<ApplyLang, readonly string[]> = {
  en: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
  es: ["lunes", "martes", "miércoles", "jueves", "viernes", "sábado", "domingo"],
};

/** A reply that means yes: Y, yes, S, si, ok. */
export function saysYes(text: string): boolean {
  return /^\s*(y|ye[sp]|yeah|s|s[ií]|ok(ay)?|claro)\b[\s.!]*$/i.test(text);
}

export type ApplicantTextKind =
  | "apply"
  | "booked"
  | "remindDay"
  | "remindSoon"
  | "confirmed"
  | "notNow"
  | "noFit"
  | "pool"
  // Phase 2: the paid trial day and the offer.
  | "trial"
  | "trialRemind"
  | "trialLead"
  | "trialScore"
  | "offer"
  // Phase 3: the background check link (Checkr).
  | "background";

export interface ApplicantTextVars {
  first: string;
  brand: string;
  /** The page to pick or change the phone screen time. */
  link?: string;
  /** When we call, like "Tuesday, Nov 10 at 2:00 PM". */
  when?: string;
  /** Just the time, like "2:00 PM". */
  time?: string;
  /** The crew member they ride with on the trial day, or the trialist for the lead. */
  lead?: string;
  /** Where the trial day starts. */
  address?: string;
}

/** Every text an applicant gets. Plain words, no colons, one ask each. */
export function applicantText(kind: ApplicantTextKind, lang: ApplyLang, v: ApplicantTextVars): string {
  const first = v.first.trim() || (lang === "es" ? "hola" : "there");
  const link = v.link ?? "";
  if (lang === "es")
    switch (kind) {
      case "apply":
        return `Hola ${first}, gracias por aplicar a ${v.brand}. Escoge una hora para una llamada corta aquí ${link}`;
      case "booked":
        return `¡Listo! ${v.brand} te llama ${v.when ?? ""}.`;
      case "remindDay":
        return `Hola ${first}, ${v.brand} te llama mañana a las ${v.time ?? ""}. Responde S para confirmar, o escoge otra hora aquí ${link}`;
      case "remindSoon":
        return `${v.brand} te llama hoy a las ${v.time ?? ""}. Responde S para confirmar.`;
      case "confirmed":
        return "¡Gracias, hablamos pronto!";
      case "notNow":
        return `Gracias por aplicar, ${first}. Ahora no tenemos un puesto que te quede bien, pero te escribimos cuando lo tengamos.`;
      case "noFit":
        return `Gracias por aplicar, ${first}. Por ahora escogimos a otra persona. ¡Te deseamos lo mejor!`;
      case "pool":
        return `Hola ${first}, somos ${v.brand}. Estamos contratando otra vez. ¿Todavía te interesa? Responde S.`;
      case "trial":
        return `Hola ${first}, tu día de prueba pagado con ${v.brand} es ${v.when ?? ""}. Encuentra a ${v.lead ?? ""} en ${v.address ?? ""}. Responde S para confirmar.`;
      case "trialRemind":
        return `Hola ${first}, tu día de prueba es mañana a las ${v.time ?? ""} en ${v.address ?? ""}. Responde S para confirmar.`;
      case "trialLead":
        return `${first} va contigo ${v.when ?? ""} para un día de prueba pagado. Enséñale cómo trabajamos. Después te mandamos una calificación corta.`;
      case "trialScore":
        return `¿Cómo le fue a ${first} hoy? Califícalo aquí ${link}`;
      case "offer":
        return `¡Buenas noticias ${first}! ${v.brand} te quiere en el equipo. Lee y firma tu oferta aquí ${link}`;
      case "background":
        return `Hola ${first}, el último paso con ${v.brand} es una revisión de antecedentes. Toma unos 10 minutos. Empieza aquí ${link}`;
    }
  switch (kind) {
    case "apply":
      return `Hi ${first}, thanks for applying to ${v.brand}! Pick a time for a quick phone call here ${link}`;
    case "booked":
      return `You're all set! ${v.brand} will call you ${v.when ?? ""}.`;
    case "remindDay":
      return `Hi ${first}, ${v.brand} will call you tomorrow at ${v.time ?? ""}. Reply Y to confirm, or pick a new time here ${link}`;
    case "remindSoon":
      return `${v.brand} will call you today at ${v.time ?? ""}. Reply Y to confirm.`;
    case "confirmed":
      return "Thanks, talk soon!";
    case "notNow":
      return `Thanks for applying, ${first}. We don't have a spot that fits right now, but we'll text you when we do.`;
    case "noFit":
      return `Thanks for applying, ${first}. We went another way for now. We wish you the best!`;
    case "pool":
      return `Hi ${first}, it's ${v.brand}. We're hiring again. Still interested? Reply Y.`;
    case "trial":
      return `Hi ${first}, your paid trial day with ${v.brand} is ${v.when ?? ""}. Meet ${v.lead ?? ""} at ${v.address ?? ""}. Reply Y to confirm.`;
    case "trialRemind":
      return `Hi ${first}, your trial day is tomorrow at ${v.time ?? ""}, ${v.address ?? ""}. Reply Y to confirm.`;
    case "trialLead":
      return `${first} rides with you ${v.when ?? ""} for a paid trial day. Show them how we work. We'll text you a short score after.`;
    case "trialScore":
      return `How did ${first} do today? Score them here ${link}`;
    case "offer":
      return `Good news ${first}! ${v.brand} would like you on the team. Read and sign your offer here ${link}`;
    case "background":
      return `Hi ${first}, the last step with ${v.brand} is a background check. It takes about 10 minutes. Start it here ${link}`;
  }
}

/** The words next to the texts box on the careers page, saved as the proof of the yes. */
export function textConsentWords(lang: ApplyLang, brand: string): string {
  return lang === "es"
    ? `Acepto mensajes de texto de ${brand} sobre mi solicitud a este número. Pueden aplicar tarifas de mensajes y datos. Responde STOP para parar.`
    : `I agree to get texts from ${brand} about my application at this number. Msg and data rates may apply. Reply STOP to stop.`;
}

/** The roles a brand hires for when the owner has not set any, by trade. Trial pay is set per role in Hiring settings. */
export function defaultRoles(trade: string): HiringRole[] {
  const role = (id: string, name: string, nameEs: string): HiringRole => ({ ...ROLE_BLANKS, id, name, nameEs });
  if (trade === "lawn") return [role("crew", "Lawn crew", "Jardinero")];
  if (trade === "washers") return [role("tech", "Washer tech", "Técnico de lavado")];
  return [role("cleaner", "House cleaner", "Limpiador o limpiadora")];
}

// Phase 2 (owner, 2026-11-04: "yes, build Phase 2") -------------------------------------------------------------------

/** The trial day scorecard the lead fills on their phone, each 1 to 5. */
export const TRIAL_SCORES = ["onTime", "quality", "speed", "attitude", "directions", "clients"] as const;
export type TrialScore = (typeof TRIAL_SCORES)[number];
export const TRIAL_SCORE_WORDS: Record<ApplyLang, Record<TrialScore, string>> = {
  en: {
    onTime: "On time",
    quality: "Quality of the work",
    speed: "Speed",
    attitude: "Attitude",
    directions: "Follows directions",
    clients: "Would clients like them",
  },
  es: {
    onTime: "Llegó a tiempo",
    quality: "Calidad del trabajo",
    speed: "Rapidez",
    attitude: "Actitud",
    directions: "Sigue instrucciones",
    clients: "¿Les caería bien a los clientes?",
  },
};
export const trialScores = z.partialRecord(z.enum(TRIAL_SCORES), z.number().int().min(1).max(5));
export type TrialScores = z.infer<typeof trialScores>;

/** A short training lesson with one question, in English and Spanish (Hiring settings, or the trade's defaults). */
export const lessonShape = z
  .object({
    id: z.string().trim().min(1).max(30),
    title: z.string().trim().min(1).max(80),
    titleEs: z.string().trim().max(80),
    body: z.string().trim().min(1).max(2000),
    bodyEs: z.string().trim().max(2000),
    question: z.string().trim().min(1).max(200),
    questionEs: z.string().trim().max(200),
    choices: z.array(z.string().trim().min(1).max(120)).min(2).max(4),
    choicesEs: z.array(z.string().trim().max(120)).max(4),
    /** Which choice is right, from 0. */
    answer: z.number().int().min(0).max(3),
  })
  .strict();
export type Lesson = z.infer<typeof lessonShape>;

const HOMES: Lesson = {
  id: "homes",
  title: "Client homes",
  titleEs: "Las casas de los clientes",
  body: "Treat every home like it is your grandma's. Don't open drawers or closets unless the job needs it. Never share a client's address, gate code or key with anyone, and never post photos of their home.",
  bodyEs:
    "Trata cada casa como si fuera la de tu abuela. No abras cajones ni closets si el trabajo no lo pide. Nunca compartas la dirección, el código de la puerta o la llave de un cliente con nadie, y nunca publiques fotos de su casa.",
  question: "A friend asks where you worked today. What do you do?",
  questionEs: "Un amigo te pregunta dónde trabajaste hoy. ¿Qué haces?",
  choices: ["Tell them the address", "Say you can't share that", "Send them a photo"],
  choicesEs: ["Le digo la dirección", "Le digo que no puedo compartir eso", "Le mando una foto"],
  answer: 1,
};
const PROBLEMS: Lesson = {
  id: "problems",
  title: "When something goes wrong",
  titleEs: "Cuando algo sale mal",
  body: "If the client is not home and you can't get in, text the office before you leave. If something breaks, take a photo and tell the office right away. Never hide it. We fix it together.",
  bodyEs:
    "Si el cliente no está y no puedes entrar, escribe a la oficina antes de irte. Si algo se rompe, toma una foto y avisa a la oficina enseguida. Nunca lo escondas. Lo arreglamos juntos.",
  question: "You break a glass. What do you do first?",
  questionEs: "Rompes un vaso. ¿Qué haces primero?",
  choices: ["Throw it away and keep going", "Take a photo and tell the office", "Wait until next time"],
  choicesEs: ["Lo boto y sigo", "Tomo una foto y aviso a la oficina", "Espero hasta la próxima vez"],
  answer: 1,
};
const DONE: Lesson = {
  id: "done",
  title: "Photos and done",
  titleEs: "Fotos y terminado",
  body: "Take before and after photos in the app. Mark the visit done before you leave. That is how the client gets their invoice, and how you get paid.",
  bodyEs:
    "Toma fotos de antes y después en la app. Marca la visita como terminada antes de irte. Así el cliente recibe su factura y así te pagan a ti.",
  question: "When do you mark the visit done?",
  questionEs: "¿Cuándo marcas la visita como terminada?",
  choices: ["Before you leave", "The next day", "Only if the client asks"],
  choicesEs: ["Antes de irme", "Al otro día", "Solo si el cliente lo pide"],
  answer: 0,
};

/** The lessons a trade starts with when the owner has not written any. One safety lesson per trade, then the same three. */
export function defaultLessons(trade: string): Lesson[] {
  const safety: Lesson =
    trade === "lawn"
      ? {
          id: "safety",
          title: "Staying safe",
          titleEs: "Seguridad",
          body: "Wear eye and ear protection. Walk the yard first and pick up rocks, toys and sticks. Never mow a wet slope, and keep people and pets away while you run equipment.",
          bodyEs:
            "Usa protección para los ojos y los oídos. Camina el patio primero y recoge piedras, juguetes y palos. Nunca cortes una loma mojada, y mantén a la gente y las mascotas lejos mientras usas las máquinas.",
          question: "What do you do before you start mowing?",
          questionEs: "¿Qué haces antes de empezar a cortar?",
          choices: ["Walk the yard and pick up rocks and toys", "Start right away", "Ask the client to do it"],
          choicesEs: ["Camino el patio y recojo piedras y juguetes", "Empiezo de una vez", "Le pido al cliente que lo haga"],
          answer: 0,
        }
      : trade === "washers"
        ? {
            id: "safety",
            title: "Staying safe",
            titleEs: "Seguridad",
            body: "Never point the wand at a person, a pet or a window up close. Check the surface first and start with low pressure on wood, siding and paint. Set the ladder on firm ground and keep three points of contact.",
            bodyEs:
              "Nunca apuntes la pistola a una persona, una mascota o una ventana de cerca. Revisa la superficie primero y empieza con poca presión en madera, siding y pintura. Pon la escalera en suelo firme y mantén tres puntos de apoyo.",
            question: "What pressure do you start with on wood?",
            questionEs: "¿Con qué presión empiezas en madera?",
            choices: ["The highest", "Low, then go up if needed", "It doesn't matter"],
            choicesEs: ["La más alta", "Baja, y subo si hace falta", "No importa"],
            answer: 1,
          }
        : {
            id: "safety",
            title: "Cleaners and safety",
            titleEs: "Productos y seguridad",
            body: "Never mix cleaners. Bleach with ammonia or with vinegar makes a gas that can hurt you. Use one product, rinse, then the next. Open a window in small rooms and wear gloves.",
            bodyEs:
              "Nunca mezcles productos. El cloro con amoníaco o con vinagre hace un gas que te puede hacer daño. Usa un producto, enjuaga, y luego el otro. Abre una ventana en cuartos pequeños y usa guantes.",
            question: "Can you mix bleach with another cleaner?",
            questionEs: "¿Puedes mezclar cloro con otro producto?",
            choices: ["Yes, it cleans better", "No, never", "Only in the shower"],
            choicesEs: ["Sí, limpia mejor", "No, nunca", "Solo en la ducha"],
            answer: 1,
          };
  return [safety, HOMES, PROBLEMS, DONE];
}

/** How they are paid, in plain words for the offer letter. */
export function offerPayWords(
  lang: ApplyLang,
  p: { payType: "percent" | "hourly"; percent?: number | null; hourlyCents?: number | null },
): string {
  if (p.payType === "hourly") {
    const rate = ((p.hourlyCents ?? 0) / 100).toFixed(2).replace(/\.00$/, "");
    return lang === "es" ? `$${rate} la hora` : `$${rate} an hour`;
  }
  const pct = String(p.percent ?? 0);
  return lang === "es"
    ? `el ${pct}% del precio de cada trabajo que hagas (se reparte con los demás en ese trabajo)`
    : `${pct}% of the price of each job you work (shared with the others on that job)`;
}

/**
 * The offer letter the office sees and can change before it goes (owner: the crew are 1099 contractors today). Plain words,
 * no colons. The office's own words are what the applicant signs.
 */
export function offerLetter(
  lang: ApplyLang,
  v: { first: string; brand: string; role: string; start: string; pay: string; payday: string },
): string {
  if (lang === "es")
    return [
      `Hola ${v.first},`,
      `${v.brand} te quiere en el equipo como ${v.role}, empezando el ${v.start}.`,
      `Vas a trabajar como contratista independiente (1099). Te pagamos ${v.pay}, más todas tus propinas. El pago sale cada ${v.payday} por Zelle, por la semana anterior, de lunes a domingo.`,
      "Tú pagas tus propios impuestos, y te mandamos un 1099 cada año. Esto no promete un número fijo de trabajos u horas, y cualquiera de los dos lo puede terminar en cualquier momento.",
      "Si te parece bien, firma abajo.",
    ].join("\n\n");
  return [
    `Hi ${v.first},`,
    `${v.brand} would like you to join the team as a ${v.role}, starting ${v.start}.`,
    `You'll work as an independent contractor (1099). You'll be paid ${v.pay}, plus all of your tips. Pay goes out every ${v.payday} by Zelle, for the week before, Monday to Sunday.`,
    "You pay your own taxes, and we send you a 1099 each year. This is not a promise of a set number of jobs or hours, and either of us can end it at any time.",
    "If this sounds right, sign below.",
  ].join("\n\n");
}

/** The paperwork steps on the applicant's page, in order. handbook shows only when the owner set a handbook link. */
export const PAPERWORK_STEPS = ["w9", "contact", "id", "handbook", "lessons"] as const;
export type PaperworkStep = (typeof PAPERWORK_STEPS)[number];

// Phase 3 (owner, 2026-11-04: "yes, build Phase 3") -------------------------------------------------------------------

/** A role's fields past its names, as a new role starts. */
export const ROLE_BLANKS = {
  trialRateCents: 0,
  /** Shown on the careers page and Google Jobs. */
  open: true,
  /** The job page's words about the work. Empty uses jobAbout. */
  about: "",
  aboutEs: "",
  /** The pay in plain words, like "You keep 35% of every clean. Tips are all yours." */
  payWords: "",
  payWordsEs: "",
  /** About what they make an hour, for Google Jobs. 0 leaves the pay off the listing. */
  payFromCents: 0,
  payToCents: 0,
  /** When the opening went up (YYYY-MM-DD), for Google Jobs. Empty uses the first of this month. */
  postedOn: "",
};
export type HiringRole = typeof ROLE_BLANKS & { id: string; name: string; nameEs: string };

/** What a role's job page says about the work when the owner has not written it, by trade. */
export function jobAbout(trade: string, lang: ApplyLang): string {
  // Only what is true for every location; the owner adds the rest in Hiring settings.
  const es = lang === "es";
  if (trade === "lawn")
    return es
      ? "Cortas el césped, limpias los bordes y recoges el jardín de nuestros clientes. Te enseñamos cómo lo hacemos en tu día de prueba pagado."
      : "You mow, edge and clean up yards for our clients. We show you how we do it on a paid trial day.";
  if (trade === "washers")
    return es
      ? "Lavas casas, entradas y patios de nuestros clientes. Te enseñamos cómo lo hacemos en tu día de prueba pagado."
      : "You wash houses, driveways and patios for our clients. We show you how we do it on a paid trial day.";
  return es
    ? "Limpias casas de nuestros clientes. Te enseñamos cómo limpiamos en tu día de prueba pagado."
    : "You clean homes for our clients. We show you how we clean on a paid trial day.";
}

/** Where a background check is. Checkr runs it (the FCRA notices too); the office decides on anything that needs a look. */
export const BACKGROUND_STATUSES = [
  "invited",
  "pending",
  "clear",
  "consider",
  "approved",
  "declined",
  "expired",
  "canceled",
] as const;
export type BackgroundStatus = (typeof BACKGROUND_STATUSES)[number];
export const BACKGROUND_WORDS: Record<BackgroundStatus, string> = {
  invited: "Link sent, waiting on them",
  pending: "Running",
  clear: "Clear",
  consider: "Needs a look",
  approved: "OK to hire",
  declined: "Not OK to hire",
  expired: "Their link ran out",
  canceled: "Canceled",
};
/** Clear, or the office looked and said OK. */
export const backgroundPassed = (s: BackgroundStatus | null | undefined): boolean => s === "clear" || s === "approved";
/** Still waiting on them or on Checkr. */
export const backgroundWaiting = (s: BackgroundStatus | null | undefined): boolean => s === "invited" || s === "pending";
